import React from 'react';
import { FormControl } from '@mui/material';

import dayjs, { Dayjs } from 'dayjs';
import { PickersDay, PickersDayProps } from '@mui/x-date-pickers/PickersDay';

import InsertInvitationIcon from '@mui/icons-material/InsertInvitation';

import { StyledDateTimePicker } from 'components/Input/styles';

import { colors } from 'theme/colors';

import { parseMoment } from 'utils/formatters/timezone';

import { ProtectorTypeDateTimeProps } from '../types';
import CustomActionBar from '../CustomPickerActionBar/CustomPickerActionBar';
import { popperSx } from './styles';

const WALL_CLOCK_FORMAT = 'YYYY-MM-DDTHH:mm:ss';

// In a zone the picker edits a UTC copy of the zone's wall clock: MUI's dayjs adapter loses the offset when an edit
// crosses a clock change, and UTC has none.
const toInstant = (value: Dayjs, timezone: string | undefined): Dayjs =>
  timezone && value.isValid() ? dayjs.tz(value.format(WALL_CLOCK_FORMAT), timezone) : value;

const toWallClock = (instant: Dayjs, timezone: string | undefined): Dayjs =>
  timezone && instant.isValid() ? dayjs.utc(instant.tz(timezone).format(WALL_CLOCK_FORMAT)) : instant;

interface Edited {
  value: Dayjs | null;
  timezone: string | undefined;
}

const inZone = ({ value, timezone: from }: Edited, to: string | undefined): Dayjs | null =>
  !value || from === to ? value : toWallClock(toInstant(value, from), to);

const ZoneNowContext = React.createContext<Dayjs | undefined>(undefined);

// MUI marks today by its own clock, which is UTC here, so the zone's today comes through context.
const ZoneDay = (props: PickersDayProps): React.ReactElement => {
  const zoneToday = React.useContext(ZoneNowContext);
  if (!zoneToday) {
    return <PickersDay {...props} />;
  }
  const today = props.day.isSame(zoneToday, 'day');
  return <PickersDay {...props} today={today} aria-current={today ? 'date' : undefined} />;
};

const readStored = (value: ProtectorTypeDateTimeProps['value'], timezone: string | undefined): Dayjs | null => {
  if (!value) {
    return null;
  }
  const moment = timezone ? parseMoment(value as string | number) : dayjs(value as string);
  return moment ? toWallClock(moment, timezone) : null;
};

export const CustomDateTimePicker = ({
  value,
  onChange,
  requiredNotFilled,
  errorMessage,
  size,
  noFutureDate,
  timezone,
  ...props
}: Omit<ProtectorTypeDateTimeProps, 'items'>): React.ReactElement => {
  const [edited, setEdited] = React.useState<Edited>();
  const stored = React.useMemo(() => readStored(value, timezone), [value, timezone]);
  const val = edited ? inZone(edited, timezone) : stored;

  const handleChange = (newValue: Dayjs | null): void => {
    if (newValue && Object.prototype.toString.call(newValue) === '[object Object]') {
      if (Number.isNaN(newValue)) {
        return;
      }
      setEdited({ value: newValue, timezone });
      onChange(toInstant(newValue, timezone).valueOf());
    } else {
      setEdited({ value: null, timezone });
      onChange(null);
    }
  };

  const [open, setOpen] = React.useState(false);

  const handleOpen = (): void => {
    setOpen(true);
  };

  const handleClose = (): void => {
    setOpen(false);
  };

  const isMobile = window.innerWidth < 768;
  const zoneNow = timezone ? toWallClock(dayjs(), timezone) : undefined;

  const commonDateTimeProps = {
    open,
    onOpen: handleOpen,
    onClose: handleClose,
    ampm: false,
    value: val,
    referenceDate: zoneNow,
    maxDateTime: noFutureDate ? (zoneNow ?? dayjs()) : undefined,
    slots: {
      actionBar: () => <CustomActionBar actions={['accept']} onAccept={() => handleClose()} />,
      toolbar: () => null,
      day: ZoneDay,
    },
    format: 'DD/MM/YYYY HH:mm',
    slotProps: {
      field: { clearable: true },
      textField: {
        helperText: errorMessage,
        InputProps: {
          ...(isMobile && {
            endAdornment: <InsertInvitationIcon htmlColor={colors.text.secondary} />,
          }),
          defaultValue: '',
        },
        placeholder: 'DD/MM/YYYY hh:mm',
        error: requiredNotFilled,
        size: size || 'small',
      },
      popper: {
        sx: popperSx,
      },
    },
    sx: {
      '& + .MuiTypography-root': {
        fontSize: '12px',
      },
    },
  };

  return (
    <FormControl fullWidth>
      <ZoneNowContext.Provider value={zoneNow}>
        <StyledDateTimePicker
          {...props}
          {...commonDateTimeProps}
          timezone={timezone ? 'UTC' : undefined}
          onChange={(date: Dayjs | null) => handleChange(date)}
        />
      </ZoneNowContext.Provider>
    </FormControl>
  );
};

export default CustomDateTimePicker;
